import { HiArrowLeft } from "react-icons/hi";
import { Link, useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import UserImage from "../../../components/portal/UserImage";
import { PortalContext, usePortalSettingSet } from "../../../context/portal-context";
import usePageTitle from "../../../hooks/use-page-title";
import { useEffect, useState, useContext, useRef } from "react";
import ApiSubmission from "../../../modules/api/submission";
import ModuleDate from "../../../modules/date";
import { ROUTES } from "../../../routes";
import Utils from "../../../utils/utils";
import { showLabelInfo } from "../../../config/methods";
import LoadingData from "../../../components/portal/LoadingData";
import ApiMessage, { TMessageItem } from "../../../modules/api/message";
import PageProgressTabReportWL from "../PageProgress/PageProgressTabReportWL";
import { SUBMISSION_WL } from '../../../config/constants';
import { MdThumbUp, MdThumbDown, MdRefresh } from "react-icons/md";
import Tooltip from "../../../components/common/Tooltip";
import { WLField } from "../../../components/dumb/WLFieldsEditor";
import { Modal } from 'react-bootstrap';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight  } from "react-icons/fi";
import FooterBoxAction from "../../../components/common/FooterBoxAction";
import ApiARIAAnalyze from "../../../modules/api/aria-analyze";
import AppUserContext from "../../../context/app-user-context";
import ModulePopup from "../../../modules/popup/popup";
import ApiARIAChat from "../../../modules/api/aria-chat";
import Notifier from "../../../modules/notifier/notifier";
import NewLineToBreak from "../../../components/static/NewLineToBreak";
import htmlToMarkdown from "@wcj/html-to-markdown";

export type HighlightedSentences = {
  [fieldId: string]: { text: string; id: string }[];
};

export default function PageMyGroupWL() {
  const title = "Student's Workplace Learning Report";
  usePageTitle(title);
  usePortalSettingSet("label_header", title);
  const { setReportUserName, setCustomText } = useContext(PortalContext);
  const user = useContext(AppUserContext);
  const location = useLocation();
  const { submissionId } = useParams();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const groupId = searchParams.get("groupId");
  const navigate = useNavigate();
  const [reportTitle, setReportTitle] = useState("Workplace Learning Report");
  const [loading, setLoading] = useState(true);
  const [loadingAria, setLoadingAria] = useState(true);
  const [loadingAriaFeedback, setLoadingAriaFeedback] = useState(true);
  const [submission, setSubmission] = useState<any>();
  const [pagination, setPagination] = useState<any>();
  const [submissionDateStatus, setSubmissionDateStatus] = useState<{ className: string; status: string } | null>(null);
  const [ariaSubmissionAnalysis, setAriaSubmissionAnalysis] = useState<any>();
  const [ariaSubmissionFeedback, setAriaSubmissionFeedback] = useState<any>();
  const [additionalPrompt, setAdditionalPrompt] = useState<string>("");
  const [feedbackSubmissionDate, setFeedbackSubmissionDate] = useState<string>("");
  const [feedbackSubmissionReadByStudentDate, setFeedbackSubmissionReadByStudentDate] = useState<string>("");
  const [feedbackSubmissionDateNoTime, setFeedbackSubmissionDateNoTime] = useState<string>("");
  const [fields, setFields] = useState<Array<WLField>>([]);
  const [messages, setMessages] = useState<TMessageItem[]>([]);
  const [isRefreshingMessages, setIsRefreshingMessages] = useState(false);
  const [refreshMessages, setRefreshMessages] = useState(false);
  const [tooltip, setTooltip] = useState({ content: null, position: null, closeButton: null });
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [highlightedSentences, setHighlightedSentences] = useState<HighlightedSentences>({});
  const [showAriaRatingModal, setShowAriaRatingModal] = useState<boolean>(true);
  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);

  useEffect(() => {
    ApiSubmission.view(submissionId, SUBMISSION_WL, groupId).then((res) => {
      if (res.status) {
        let submission = res.data.submission;
        let pagination = res.data.pagination;
        const submissionEndData = ModuleDate.getTitleFormat(submission.submissionDate?.to);

        setSubmission(submission);
        setPagination(pagination);
        setReportUserName(submission.owner?.name || "-");
        setReportTitle(submissionEndData);
        setCustomText(`Workplace Learning Report (${submissionEndData})`);

        let fields = submission.cohort?.wlFormat?.fields;
        setFields(Array.isArray(fields) ? fields : []);
        if (submission?.submissionDate?.to && submission?.modifiedDate) {
          setSubmissionDateStatus(ModuleDate.getSubmissionStatus(submission.submissionDate.to, submission.modifiedDate));
        }
      }

      setLoading(false);
    });
  }, []);

  // it's considered short if it's less than 100 words
  const isReportTooShort = (submission) => {
    try {
      let words = 0;
      const content = JSON.parse(submission.content);
      const fields = submission?.cohort?.wlFormat?.fields;

      if (Array.isArray(fields) && fields.length > 0) {
        fields.forEach(field => {
          const value = Utils.html2Text(content[field._id]);
          words += value.trim().split(/\s+/).filter(i => i !== "").length;
        });
      }

      return words < 100;
    } catch (ignore) {
      return false;
    }
  };

  useEffect(() => {
    if (submission) {
      setLoadingAria(true);
      setLoadingAriaFeedback(true);

      (async () => {
        const content = JSON.parse(submission.content ?? "{}")
        const reportData = await formatReportContent(content);

        // Get Aria Analysis Report
        ApiARIAAnalyze.analyzeStudentReportForTeacher(submission.createdUserId, submissionId, getFieldsDescriptions(), reportData)
          .then((res) => {
            if (res.status) {
              setAriaSubmissionAnalysis({
                ...res.data,
                report_too_short: res.data.report_too_short || isReportTooShort(submission)
              });

              // Get Aria Feedback only after analysis is complete
              return ApiARIAAnalyze.getStudentReportFeedback(submission.createdUserId, submissionId, "", getHighlightedTexts(highlightedSentences));
            }
            throw new Error("Failed to analyze student report.");
          })
          .then((res) => {
            if (res.status) {
              setAriaSubmissionFeedback(res.data);
            }
          })
          .catch((error) => {
            console.error("Error fetching Aria analysis or feedback:", error);
          })
          .finally(() => {
            setLoadingAria(false);
            setLoadingAriaFeedback(false);
          });
      })();
    }
  }, [submission]);

  useEffect(() => {
    if (loading) return;
    (async () => {
      setIsRefreshingMessages(true);
      const messages = await ApiMessage.getMessagesBySubmission(submissionId);
      setMessages(messages.messages);
      await Utils.sleep(1000);
      setIsRefreshingMessages(false);
    })();
  }, [loading, refreshMessages]);

  useEffect(() => {
    const handleNonDraftFeedback = async () => {
      if (messages && messages.length > 0) {
        const nonDraftMessages = messages.filter(message => message.status !== "Draft");

        if (nonDraftMessages.length > 0) {
          const latestNonDraft = nonDraftMessages.sort(
            (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
          )[0];

          setFeedbackSubmissionDate(ModuleDate.getSubmissionDateFormat(latestNonDraft?.createdDate));
          setFeedbackSubmissionDateNoTime(ModuleDate.getSubmissionDateFormat(latestNonDraft?.createdDate, true));

          const res = await ApiMessage.getSubmissionReadStatus(latestNonDraft._id);
          if (res.status) {
            setFeedbackSubmissionReadByStudentDate(ModuleDate.getSubmissionDateFormat(res?.data?.readDate));
          } else {
            console.error("Failed to update read status:", res.message);
          }
        }
      }
    };

    handleNonDraftFeedback();
  }, [messages]);


  const goBack = (e: any) => {
    e.preventDefault();
    if (window.history.length === 1) {
      navigate(ROUTES.PORTAL_DASHBOARD);
    } else {
      navigate(-1)
    }
    // if (location.state) {
    //   if (location.state.from) {
    //     navigate(location.state.from);
    //   } else if (typeof submission?.owner?._id === "string") {
    //     const ownerId = submission.owner._id;
    //     const stateGroupId = location.state.groupId;
    //     navigate(
    //       `${ROUTES.PORTAL_MY_GROUP_DETAILS}/${stateGroupId}/${ownerId}?type=${SUBMISSION_WL}`,
    //       { state: location.state }
    //     );
    //   } else {
    //     navigate(ROUTES.PORTAL_MY_GROUP, { state: location.state });
    //   }
    //   return;
    // }
    // if (userId && groupId) {
    //   navigate(
    //     `${ROUTES.PORTAL_MY_GROUP_DETAILS}/${groupId}/${userId}?type=${SUBMISSION_WL}`
    //   );
    // } else {
    //   navigate(ROUTES.PORTAL_MY_GROUP);
    // }
  };

  const handleMouseEnter = (e: any, content: any, closeButton = true) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const rect = e.target.getBoundingClientRect();
    setTooltip({
      content,
      position: {
        top: rect.top + window.scrollY - 10,
        left: rect.left + window.scrollX + rect.width / 2,
      },
      closeButton,
    });
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setTooltip({ content: null, position: null, closeButton: null });
    }, 100);
  };

  const handleTooltipMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTooltipMouseLeave = () => {
    setTooltip({ content: null, position: null, closeButton: null });
  };

  const getSentimentIcon = (sentiment: string): string => {
    switch (sentiment) {
      case 'Positive':
        return '/assets/images/icon/icon-reaction-good.svg';
      case 'Negative':
        return '/assets/images/icon/icon-reaction-mad.svg';
      case 'Neutral':
        return '/assets/images/icon/icon-reaction-neutral.svg';
      default:
        return '/assets/images/icon/icon-reaction-neutral.svg';
    }
  };

  const createListItems = (content: string | Record<string, any> = ''): JSX.Element[] => {
    if (!content) return [];

    if (typeof content === 'string') {
      // If it's a string, split by newlines
      // const items = text.split(/(?:\.\s+|\n+)/).filter(item => item.trim());
      const items = content.split(/\n+/).filter(item => item.trim());
      return items.map((item, index) => <li key={index}>{item.trim()}</li>);
    }

    if (typeof content === 'object') {
      const items: string[] = [];

      if (content?.content?.analysis) {
        Object.entries(content.content.analysis).forEach(([key, section]: [string, any]) => {
          if (section.details && section.rating) {
            items.push(`${key} (${section.rating}): ${section.details}`);
          }
        });
      }

      if (content.overall_summary) {
        items.push(`Overall Summary: ${content.overall_summary}`);
      }

      if (content.rubricResultString) {
        items.push(`Overall Summary: ${content.rubricResultString}`);
      }

      return items.map((item, index) => <li key={index}>{item.trim()}</li>);
    }

    return [];
  };

  const handleRegenerateAnalysis = () => {
    return async () => {
      setLoadingAria(true);
      const content = JSON.parse(submission.content ?? "{}")
      const reportData = await formatReportContent(content);

      const res = await ApiARIAAnalyze.regenerateAnalyzeStudentReportForTeacher(submission.createdUserId, submissionId, getFieldsDescriptions(), reportData);
      if (res.status) {
        setAriaSubmissionAnalysis({
          ...res.data,
          report_too_short: res.data.report_too_short || isReportTooShort(submission)
        });
      }
      setLoadingAria(false);
    };
  }

  const convertToMarkdown = async (html: string) => {
    let content = await htmlToMarkdown({ html });
    content = content.replace(/^# /gm, '## '); // Convert H1 (`# `) to H2 (`## `) in content only
    return content;
  };

  const formatReportContent = async (formData: any) => {
    const formattedReportObject: { [key: string]: string } = {};
    if (fields.length > 0) {
      for (const field of fields) {
        const fieldId = field._id;
        const fieldName = field.name.replace(/\s+/g, '');
        const fieldValue = formData[fieldId] || '';
        formattedReportObject[fieldName] = await convertToMarkdown(fieldValue);
      }
    } else {
      formattedReportObject['summaryKeyActivities'] = await convertToMarkdown(formData?.data?.summaryKeyActivities || '');
      formattedReportObject['detailsLearning'] = await convertToMarkdown(formData?.data?.detailsLearning || '');
      formattedReportObject['reflection'] = await convertToMarkdown(formData?.data?.reflection || '');
    }
    return formattedReportObject;
  };

  const handleRegenerateFeedback = () => {
    return async () => {
      setLoadingAriaFeedback(true);
      const res = await ApiARIAAnalyze.regenerateStudentReportFeedback(submission.createdUserId, submissionId, additionalPrompt, getHighlightedTexts(highlightedSentences));
      if (res.status) {
        setAriaSubmissionFeedback(res.data);
      }
      setLoadingAriaFeedback(false);
    };
  }

  const getHighlightedTexts = (highlightedSentences: HighlightedSentences): string[] => {
    return Object.values(highlightedSentences)
      .flat()
      .map((highlight) => highlight.text);
  };

  const getFieldsDescriptions = (): { [key: string]: string } => {
    if (fields.length > 0) {
      return fields.reduce((acc, field) => {
        acc[field.name.replace(/\s+/g, '')] = field.description || '';
        return acc;
      }, {} as { [key: string]: string });
    } else {
      return {
        summaryKeyActivities: '',
        detailsLearning: '',
        reflection: ''
      };
    }
  };

  const handleAriaRatingModal = (like: boolean) => {
    ModulePopup.showFeedbackAlert({
      title: "Rate ARIA’s Summary",
      description: "How would you rate your experience with ARIA?",
      buttonLabel: "Send Feedback",
      defaultRating: like ? "like" : "dislike",
      onSubmit: async (feedback) => {
        const rateAriaChat = await ApiARIAChat.rateARIAChat(user.id, submissionId, feedback.comment, feedback.isLiked, user.role);
        if (rateAriaChat.status) {
          Notifier.success("Feedback submitted. Thank you!");
        } else {
          Notifier.error("An error occurred");
        }
        setShowAriaRatingModal(false);
      },
    });
  };

  // useEffect(() => {
  //   const getSubmissionRating = async () => {
  //     if (submissionId) {
  //       const ariaRating = await ApiARIAChat.getAriaRating(user.id, submissionId);
  //       if (ariaRating.status) setShowAriaRatingModal(false);
  //     }
  //   }
  //   getSubmissionRating();
  // }, [submissionId]);

  return (
    <>
      <div className="d-flex gap-2 mb-3 d-none">
        <h1 className="fs-title">
          {/* <Link
            to={""}
            className="text-primary p-0 me-2 hide-mobile-down"
            title="Go back"
            onClick={goBack}
          >
            <HiArrowLeft />
          </Link> */}
          {reportTitle}
        </h1>
      </div>

      <LoadingData show={loading} />

      {!loading && !submission && (
        <p className="alert alert-warning">Submission not found.</p>
      )}

      {!loading && submission && (
        <>
          {/* Old Code */}
          {/* <div className="d-flex gap-2 align-items-center mb-3">
            <div className="d-flex justify-content-center align-items-center gap-2">
              <UserImage width={67} url={submission?.owner?.avatar} />
              <div className="ms-2">
                <h1 className="fs-title m-0">
                  {showLabelInfo(submission?.owner?.name)}
                </h1>
                <span>{showLabelInfo(submission?.owner?.email)}</span>
              </div>
            </div>
          </div> */}

          {/* Profile */}
          <div className="mgr__profile-details">
            <div>
              <h1 className="mgr__profile-details__title">Student Details</h1>
              <div className="mgr__profile-details__info">
                <p className="label">Name</p><span className="separator">:</span><p className="value">{submission?.owner?.name}</p>
                <p className="label">Email</p><span className="separator">:</span><p className="value value--normal">{ submission?.owner?.email }</p>
                <p className="label">Employer</p><span className="separator">:</span><p className="value">{ submission?.owner?.organization }</p>
                <p className="label">Job Title</p><span className="separator">:</span><p className="value">{ submission?.owner?.jobTitle }</p>
                <p className="label">Job Description</p><span className="separator">:</span>
                <p className="value">
                { submission?.owner?.jobDescription ?
                    (<>
                      <a style={{ cursor: "pointer" }} onClick={handleShowModal}>View</a>
                        <Modal show={showModal} onHide={handleCloseModal} centered backdrop="static">
                          <Modal.Header closeButton>
                            <Modal.Title>Job Description</Modal.Title>
                          </Modal.Header>
                          <Modal.Body>
                            <p>
                              <NewLineToBreak value={submission?.owner?.jobDescription} />
                            </p>
                          </Modal.Body>
                        </Modal>
                      </>
                    )
                    :
                  <span>-</span>
                  }
                </p>
              </div>
            </div>
            <div>
              <h1 className="mgr__profile-details__title">Submission Details</h1>
              <div className="mgr__profile-details__info">
                <p className="label">Submission Date</p><span className="separator">:</span><p className="value">
                  {ModuleDate.getSubmissionDateFormat(submission?.modifiedDate)}
                </p>
                <p className="label">Deadline</p><span className="separator">:</span><p className="value">
                  {ModuleDate.getSubmissionDateFormat(submission?.submissionDate?.to)}
                </p>
                <p className="label">Submission Status</p><span className="separator">:</span>
                <p className={`value ${submissionDateStatus?.className || ""}`}>
                  {submissionDateStatus?.status || ""}
                </p>
              </div>
            </div>
            <div>
              <h1 className="mgr__profile-details__title">Feedback Details</h1>
              <div className="mgr__profile-details__info">
                <p className="label">Feedback Status</p><span className="separator">:</span>
                {feedbackSubmissionDate
                  ? <p className="value positive">Given</p>
                  : <p className="value negative">Pending</p>}

                <p className="label">Feedback Date</p><span className="separator">:</span>
                <p className="value">{feedbackSubmissionDate?.trim() || "-"}</p>

                <p className="label">Read by Student</p><span className="separator">:</span><p className="value">{feedbackSubmissionReadByStudentDate?.trim() || "-"}</p>
              </div>
            </div>
          </div>
          {/* Reflections */}
          <div className="box-default bg-white mb-3 mt-4">
            <PageProgressTabReportWL
              submission={submission}
              fields={fields}
              messages={messages}
              setMessages={setMessages}
              refreshMessages={setRefreshMessages}
              isRefreshingMessages={isRefreshingMessages}
              reportTitle={ModuleDate.getTitleFormat(submission?.modifiedDate)}
              supervisorFeedback={ariaSubmissionFeedback?.feedback ?? ariaSubmissionAnalysis?.result?.content?.supervisor_recommendation}
              loadingAria={loadingAriaFeedback || loadingAria}
              additionalPrompt={additionalPrompt}
              setAdditionalPrompt={setAdditionalPrompt}
              handleRegenerateAnalysis={handleRegenerateAnalysis()}
              handleRegenerateFeedback={handleRegenerateFeedback()}
              highlightedSentences={highlightedSentences}
              setHighlightedSentences={setHighlightedSentences}
              report_too_short={ariaSubmissionAnalysis?.report_too_short === true}
              report_summary={ariaSubmissionAnalysis?.result?.content?.report_summary}
              sentimentIcon={getSentimentIcon(ariaSubmissionAnalysis?.result?.content?.sentiment_analysis?.sentiment)}
              sentimentClass={ariaSubmissionAnalysis?.result?.content?.sentiment_analysis?.sentiment ?? "pending"}
              // sentimentClass={"Positive"} // This is a placeholder
              sentiment={ariaSubmissionAnalysis?.result?.content?.sentiment_analysis?.sentiment}
              sentiment_analysis_rationale={createListItems(ariaSubmissionAnalysis?.result?.content?.sentiment_analysis?.rationale)}
              supervisor_recommendation={ariaSubmissionAnalysis?.result?.content?.supervisor_recommendation}
              feedbackSubmissionDate={feedbackSubmissionDateNoTime}
            />
          </div>
          {/* ARIA Review */}
          {/* <div className="mgr__aria-summary d-none">
            {!loadingAria ?
              <>
                <div className="mgr__aria-summary__header">
                  <div className="mgr__aria-summary__header__profile">
                    <img src="/assets/images/aria-profile.png" alt="ARIA" />
                    <div>
                      <h1>ARIA</h1>
                      <p>Here is the summary for { submission?.owner?.name }</p>
                    </div>
                  </div>
                </div>
                <div className="mgr__aria-summary__details">
                  <div className="mgr__aria-summary__left">
                    <div className="mgr__aria-summary__item">
                      <h1>Report Criteria</h1>
                      <ul>
                      {createListItems(ariaSubmissionAnalysis?.result?.content?.report_evaluation)}
                      </ul>
                    </div>
                    <div className="mgr__aria-summary__item">
                      <h1>
                        Sentiment Analysis : <img src={getSentimentIcon(ariaSubmissionAnalysis?.result?.content?.sentiment_analysis?.sentiment)} alt="" />
                        <span className={ariaSubmissionAnalysis?.result?.content?.sentiment_analysis?.sentiment.toLowerCase()}>{ariaSubmissionAnalysis?.result?.content?.sentiment_analysis?.sentiment}</span>
                      </h1>
                      <ul>
                        {createListItems(ariaSubmissionAnalysis?.result?.content?.sentiment_analysis?.rationale)}
                      </ul>
                    </div>
                  </div>
                  <div className="mgr__aria-summary__right">
                    <div className="mgr__aria-summary__item">
                      <h1>Report Summary</h1>
                      <ul>
                        {createListItems(ariaSubmissionAnalysis?.result?.content?.report_summary)}
                      </ul>
                    </div>
                    <div className="mgr__aria-summary__item">
                      <h1>Recommendations for Supervisor</h1>
                      <ul>
                        {createListItems(ariaSubmissionAnalysis?.result?.content?.supervisor_recommendation)}
                      </ul>
                    </div>
                  </div>
                </div>
                {showAriaRatingModal &&
                  <div className="mgr__aria-summary__header__buttons">
                    <h1>Rate ARIA’s Summary</h1>
                    <span className="thumb-up" onClick={()=> handleAriaRatingModal(true)}><MdThumbUp /></span>
                    <span className="thumb-down" onClick={()=> handleAriaRatingModal(false)}><MdThumbDown /></span>
                  </div>
                }
                <div className="d-flex justify-content-center my-3">
                  <span onClick={handleRegenerateAnalysis()} style={{cursor: "pointer"}}><MdRefresh /> Generate again</span>
                </div>
              </>
              :
              <div className='ac__chat__typing'>
                <span></span>
                <span></span>
                <span></span>
              </div>
            }
          </div> */}
          <FooterBoxAction
            onBackUrlClick={goBack}
            rightItem={
              <div className="d-flex gap-1">
                <button className="btn btn-dark bba__btn-icon"
                  disabled={!pagination || !pagination.prevStudentSubmissionId}
                  onMouseEnter={(e) => handleMouseEnter(e, "Previous Student", false)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => navigate(`${ROUTES.PORTAL_MY_GROUP_REPORT_WL}/${pagination.prevStudentSubmissionId}`)}
                >
                  <FiChevronsLeft />
                </button>
                <button className="btn btn-dark bba__btn-icon"
                  disabled={!pagination || !pagination.prevSubmissionId}
                  onMouseEnter={(e) => handleMouseEnter(e, "Previous Report", false)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => navigate(`${ROUTES.PORTAL_MY_GROUP_REPORT_WL}/${pagination.prevSubmissionId}`)}
                >
                  <FiChevronLeft />
                </button>
                <button className="btn btn-dark bba__btn-icon"
                  disabled={!pagination || !pagination.nextSubmissionId}
                  onMouseEnter={(e) => handleMouseEnter(e, "Next  Report", false)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => navigate(`${ROUTES.PORTAL_MY_GROUP_REPORT_WL}/${pagination.nextSubmissionId}`)}
                >
                  <FiChevronRight />
                </button>
                <button className="btn btn-dark bba__btn-icon"
                  disabled={!pagination || !pagination.nextStudentSubmissionId}
                  onMouseEnter={(e) => handleMouseEnter(e, "Next  Student", false)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => navigate(`${ROUTES.PORTAL_MY_GROUP_REPORT_WL}/${pagination.nextStudentSubmissionId}`)}
                >
                  <FiChevronsRight />
                </button>
                <Tooltip
                  content={tooltip.content}
                  position={tooltip.position}
                  closeButton={tooltip.closeButton}
                  onClose={handleTooltipMouseLeave}
                  onMouseEnter={handleTooltipMouseEnter}
                  onMouseLeave={handleTooltipMouseLeave}
                />
              </div>
            }
          />
        </>
      )}
    </>
  );
}
